import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db, functions } from '../firebaseConfig';
import { doc, getDoc, collection, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { IoDiamondOutline, IoStarOutline, IoGlobeOutline, IoCameraOutline, IoCardOutline } from 'react-icons/io5';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import './ProveedorServiciosScreen.css'; // Reuses page layout, loader, and responsive styles

function ProveedorSuscripcionScreen() {
    const navigate = useNavigate();
    const { userData } = useAuth();
    const [providerData, setProviderData] = useState(null);
    const [marketPlanes, setMarketPlanes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    
    // Addon request states
    const [addonRequest, setAddonRequest] = useState({ show: false, addonType: '', notes: '' });
    const [feedback, setFeedback] = useState({ show: false, title: '', message: '', type: 'success' });

    useEffect(() => {
        if (!userData) return;
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userData]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const providerId = userData.proveedorId;
            if (providerId) {
                const provSnap = await getDoc(doc(db, 'market_proveedores', providerId));
                if (provSnap.exists()) setProviderData(provSnap.data());
            }

            const planesSnap = await getDocs(collection(db, 'market_planes'));
            const planesList = planesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
            planesList.sort((a, b) => (a.orden || 0) - (b.orden || 0));
            setMarketPlanes(planesList);
        } catch (e) {
            console.error("Error loading B2B plan data:", e);
        }
        setLoading(false);
    };

    const handleSubscribe = async (planId) => {
        setSubmitting(true);
        try {
            const createSession = httpsCallable(functions, 'createB2BSubscriptionSession');
            const result = await createSession({
                planId,
                successUrl: `${window.location.origin}/proveedor/suscripcion?success=true`,
                cancelUrl: `${window.location.origin}/proveedor/suscripcion?cancel=true`
            });

            if (result.data.success) {
                if (result.data.sessionUrl) {
                    window.location.href = result.data.sessionUrl;
                } else {
                    setFeedback({
                        show: true,
                        title: "Plan Actualizado",
                        message: "Te has cambiado con éxito al plan gratuito Plata.",
                        type: "success"
                    });
                    fetchData();
                }
            } else {
                throw new Error("No se pudo iniciar la sesión de suscripción.");
            }
        } catch (e) {
            setFeedback({
                show: true,
                title: "Error",
                message: e.message || "Ocurrió un error al intentar suscribirte.",
                type: "error"
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleRequestAddon = async () => {
        if (!addonRequest.notes.trim()) {
            alert("Por favor escribe una breve nota describiendo tus necesidades.");
            return;
        }

        setSubmitting(true);
        try {
            const providerId = userData.proveedorId;
            await addDoc(collection(db, 'market_addon_solicitudes'), {
                providerId,
                providerName: providerData?.nombrePublico || providerData?.nombreContacto,
                tipoAddon: addonRequest.addonType,
                notas: addonRequest.notes,
                fechaSolicitud: serverTimestamp(),
                estatus: 'pendiente'
            });

            setAddonRequest({ show: false, addonType: '', notes: '' });
            setFeedback({
                show: true,
                title: "Solicitud Enviada 📧",
                message: `Tu solicitud para ${addonRequest.addonType === 'sitio_web' ? 'Sitio Web Corporativo' : 'Fotógrafo Profesional'} ha sido enviada. Un superusuario se pondrá en contacto contigo pronto.`,
                type: "success"
            });
        } catch (e) {
            alert("Error al enviar solicitud: " + e.message);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', minHeight: '100vh', justifyContent: 'center', alignItems: 'center' }}>
                <div className="loader"></div>
                <h3 style={{ marginLeft: 15 }}>Cargando planes...</h3>
            </div>
        );
    }

    const currentPlanId = providerData?.marketPlanId || 'tier_plata';

    return (
        <div className="ks-page">
            <KondoSidebar activeRoute="/proveedor/suscripcion" />
            <main className="ks-main">
                <div className="ks-header-sticky-wrapper">
                    <KondoBrandHeader showBack={false} />
                </div>
                <div className="ks-scroll-area">
                    <div className="muro-header-stitch">
                        <div className="muro-hero-card">
                            <div className="muro-hero-content">
                                <h1>Gestionar Plan</h1>
                                <p>Mi Suscripción B2B y Beneficios</p>
                            </div>
                            <IoCardOutline className="muro-hero-icon-decor" />
                        </div>
                    </div>

                    <div className="provider-services-container" style={{ padding: '0 20px 40px 20px', maxWidth: '1000px', margin: '0 auto' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px', marginBottom: '40px', marginTop: '20px' }}>
                            {marketPlanes.map(plan => {
                                const isCurrent = plan.id === currentPlanId;
                                const isFree = Number(plan.cuotaMensual) === 0;
                                const isSocio = plan.id.toLowerCase().includes('socio');

                                let headerBg = 'linear-gradient(135deg, #64748b, #94a3b8)';
                                let borderTop = 'none';
                                let headerText = 'white';

                                if (isSocio) {
                                    headerBg = 'linear-gradient(135deg, #090d16, #1e1b4b)';
                                    borderTop = '4px solid #4f46e5'; // Purple highlight
                                    headerText = '#a5b4fc';
                                } else if (plan.id === 'tier_diamante') {
                                    headerBg = 'linear-gradient(135deg, #1e3a8a, #3b82f6)';
                                    borderTop = '4px solid #60a5fa';
                                } else if (plan.id === 'tier_oro') {
                                    headerBg = 'linear-gradient(135deg, #ca8a04, #eab308)';
                                    borderTop = '4px solid #fef08a';
                                }

                                return (
                                    <div 
                                        key={plan.id}
                                        style={{
                                            background: 'white',
                                            borderRadius: '16px',
                                            boxShadow: isCurrent ? '0 10px 15px -3px rgba(79, 70, 229, 0.1), 0 4px 6px -2px rgba(79, 70, 229, 0.05)' : '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                                            border: isCurrent ? '2px solid #4f46e5' : '1px solid #e2e8f0',
                                            borderTop: borderTop !== 'none' ? borderTop : undefined,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'space-between',
                                            overflow: 'hidden',
                                            position: 'relative',
                                            minHeight: '430px'
                                        }}
                                    >
                                        <div style={{ background: headerBg, color: headerText, padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 'bold' }}>Tier {plan.nombre}</h3>
                                            {isCurrent && (
                                                <span style={{ background: 'rgba(255, 255, 255, 0.25)', color: 'white', padding: '3px 8px', borderRadius: '10px', fontSize: '0.7rem', fontWeight: 'bold' }}>
                                                    Activo
                                                </span>
                                            )}
                                        </div>

                                        <div style={{ padding: '24px', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                            <div>
                                                <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9', paddingBottom: '8px', fontSize: '0.9rem' }}>
                                                    <span style={{ color: '#64748b' }}>Cuota Mensual</span>
                                                    <strong style={{ fontSize: '1.05rem', color: '#0f172a' }}>
                                                        {isSocio ? 'Por invitación' : (isFree ? 'Gratis' : `$${plan.cuotaMensual} MXN`)}
                                                    </strong>
                                                </div>

                                                <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9', paddingBottom: '8px', fontSize: '0.9rem' }}>
                                                    <span style={{ color: '#64748b' }}>Comisión</span>
                                                    <strong style={{ fontSize: '1.05rem', color: '#10b981' }}>
                                                        {plan.comisionPorcentaje}%
                                                    </strong>
                                                </div>

                                                <div style={{ color: '#475569', fontSize: '0.85rem' }}>
                                                    <strong style={{ display: 'block', marginBottom: '6px', color: '#334155' }}>Características:</strong>
                                                    <ul style={{ margin: 0, paddingLeft: '18px', lineHeight: '1.5' }}>
                                                        {Array.isArray(plan.features) ? plan.features.map((f, i) => <li key={i} style={{ marginBottom: '4px' }}>{f}</li>) : <li>{plan.features}</li>}
                                                    </ul>
                                                </div>
                                            </div>

                                            <button
                                                disabled={isCurrent || submitting || isSocio}
                                                onClick={() => handleSubscribe(plan.id)}
                                                style={{
                                                    background: isCurrent ? '#f1f5f9' : (isSocio ? '#e2e8f0' : (isFree ? '#94a3b8' : '#4f46e5')),
                                                    color: isCurrent || isSocio ? '#94a3b8' : 'white',
                                                    border: 'none',
                                                    padding: '12px',
                                                    borderRadius: '8px',
                                                    width: '100%',
                                                    fontWeight: 'bold',
                                                    cursor: isCurrent || submitting || isSocio ? 'not-allowed' : 'pointer',
                                                    marginTop: '20px',
                                                    fontSize: '0.9rem'
                                                }}
                                            >
                                                {isCurrent ? 'Plan Actual' : (isSocio ? 'Solo Invitación' : (isFree ? 'Bajar a Gratis' : 'Suscribirse'))}
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', padding: '30px' }}>
                            <h2 style={{ margin: '0 0 10px 0', fontSize: '1.4rem', color: '#1e1b4b' }}>Servicios Premium Adicionales</h2>
                            <p style={{ margin: '0 0 25px 0', color: '#64748b', fontSize: '0.9rem' }}>Potencia tu presencia digital. Solicita diseño web profesional o sesiones fotográficas para tus servicios.</p>

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px' }}>
                                <div style={{ border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                    <div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                                            <IoGlobeOutline size={22} color="#4f46e5" />
                                            <h4 style={{ margin: 0, fontSize: '1rem', color: '#1f2937' }}>Sitio Web Corporativo</h4>
                                        </div>
                                        <p style={{ margin: '5px 0 15px 0', fontSize: '0.85rem', color: '#475569', lineHeight: '1.4' }}>Creación de una landing page optimizada para recibir prospectos de forma directa en internet.</p>
                                    </div>
                                    <button onClick={() => setAddonRequest({ show: true, addonType: 'sitio_web', notes: '' })} style={{ background: '#f1f5f9', color: '#4f46e5', border: 'none', padding: '8px 12px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.85rem' }}>
                                        Solicitar Información
                                    </button>
                                </div>

                                <div style={{ border: '1px solid #e2e8f0', borderRadius: '12px', padding: '20px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                    <div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                                            <IoCameraOutline size={22} color="#4f46e5" />
                                            <h4 style={{ margin: 0, fontSize: '1rem', color: '#1f2937' }}>Fotografía Profesional</h4>
                                        </div>
                                        <p style={{ margin: '5px 0 15px 0', fontSize: '0.85rem', color: '#475569', lineHeight: '1.4' }}>Sesión fotográfica profesional en alta definición para tu portafolio y logo de servicio.</p>
                                    </div>
                                    <button onClick={() => setAddonRequest({ show: true, addonType: 'fotografia', notes: '' })} style={{ background: '#f1f5f9', color: '#4f46e5', border: 'none', padding: '8px 12px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.85rem' }}>
                                        Agendar Sesión
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {addonRequest.show && (
                        <div className="modal-overlay" style={{ zIndex: 10950, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)' }}>
                            <div className="modal-content" style={{ maxWidth: '450px', background: 'white', borderRadius: '16px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)', padding: '0', overflow: 'hidden' }}>
                                <div className="modal-header" style={{ padding: '20px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <h3 style={{ margin: 0, color: '#1e293b', fontSize: '1.2rem', fontWeight: 'bold' }}>
                                        Solicitar {addonRequest.addonType === 'sitio_web' ? 'Sitio Web B2B' : 'Fotógrafo B2B'}
                                    </h3>
                                    <button onClick={() => setAddonRequest({ show: false, addonType: '', notes: '' })} style={{ background: 'none', border: 'none', color: '#64748b', fontSize: '1.5rem', cursor: 'pointer', lineHeight: 1 }}>&times;</button>
                                </div>
                                <div className="modal-body" style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                    <label style={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#334155' }}>Describe brevemente tus requerimientos o dudas:</label>
                                    <textarea 
                                        rows={4}
                                        value={addonRequest.notes}
                                        onChange={e => setAddonRequest({ ...addonRequest, notes: e.target.value })}
                                        placeholder="Ej: Hola, me interesa el paquete corporativo de sitio web y me gustaría saber tiempos de entrega..."
                                        style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #cbd5e1', boxSizing: 'border-box', fontFamily: 'inherit' }}
                                    />
                                </div>
                                <div className="modal-footer" style={{ padding: '15px 24px', background: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                                    <button onClick={() => setAddonRequest({ show: false, addonType: '', notes: '' })} style={{ background: 'white', border: '1px solid #cbd5e1', color: '#475569', padding: '10px 18px', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' }}>
                                        Cancelar
                                    </button>
                                    <button onClick={handleRequestAddon} disabled={submitting} style={{ background: '#4f46e5', color: 'white', border: 'none', padding: '10px 18px', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' }}>
                                        {submitting ? 'Enviando...' : 'Enviar Solicitud'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {feedback.show && (
                        <div className="modal-overlay" style={{ zIndex: 10960, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)' }}>
                            <div className="modal-content" style={{ maxWidth: '400px', background: 'white', borderRadius: '16px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)', padding: '0', overflow: 'hidden' }}>
                                <div className="modal-header" style={{ padding: '20px', background: feedback.type === 'success' ? '#ecfdf5' : '#fef2f2', borderBottom: '1px solid ' + (feedback.type === 'success' ? '#a7f3d0' : '#fecaca'), display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <h3 style={{ margin: 0, color: feedback.type === 'success' ? '#065f46' : '#991b1b', fontSize: '1.15rem', fontWeight: 'bold' }}>
                                        {feedback.title}
                                    </h3>
                                    <button onClick={() => setFeedback({ ...feedback, show: false })} style={{ background: 'none', border: 'none', color: feedback.type === 'success' ? '#065f46' : '#991b1b', fontSize: '1.5rem', cursor: 'pointer', lineHeight: 1 }}>&times;</button>
                                </div>
                                <div className="modal-body" style={{ padding: '24px', fontSize: '0.95rem', color: '#475569', textAlign: 'center' }}>
                                    <p style={{ margin: 0, lineHeight: '1.5' }}>
                                        {feedback.message}
                                    </p>
                                </div>
                                <div className="modal-footer" style={{ padding: '15px 24px', background: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'center' }}>
                                    <button 
                                        onClick={() => setFeedback({ ...feedback, show: false })} 
                                        style={{ 
                                            background: feedback.type === 'success' ? '#10b981' : '#ef4444', 
                                            color: 'white', 
                                            border: 'none', 
                                            padding: '10px 24px', 
                                            borderRadius: '8px', 
                                            fontWeight: '600', 
                                            cursor: 'pointer' 
                                        }}
                                    >
                                        Aceptar
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <KondoBottomNav activeRoute="/proveedor/suscripcion" />
            </main>
        </div>
    );
}

export default ProveedorSuscripcionScreen;
